import { useState, useEffect, useCallback, useContext } from 'react';
import useYoutubeConnection from '@features/Socials/Youtube/useYoutubeConnection';
import { analytics } from '@services/analytics';
import { TeamContext } from '@services/Session';

export type YoutubeVariant = 'team-page' | 'livestream-setup' | 'team-settings';

interface YoutubeLinkingOptions {
  variant: YoutubeVariant;
  liveTeamId?: string | null;
}

export function useYoutubeLinking({
  variant,
  liveTeamId = null,
}: YoutubeLinkingOptions) {
  const teamStore = useContext(TeamContext);
  const teamId = liveTeamId ? liveTeamId : teamStore.id;
  const {
    signIn,
    cancelSignin,
    status,
    onUnlinkYoutubeClick,
    showPrivacyModal,
    setShowPrivacyModal,
    showVideoSettings,
    setShowVideoSettings,
    updateUnlistedSetting,
  } = useYoutubeConnection({ liveTeamId });
  const sportType = teamStore.sport;
  const [showIntro, setShowIntro] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Sync status to modals
  useEffect(() => {
    if (!status) {
      return;
    }

    if (status.phase === 'completed') {
      setShowSuccess(true);

      // Team-page variant tracks when linking completes
      if (variant === 'team-page' && status.channel) {
        analytics.track('Stream Destination Linked', {
          teamId,
          sport: sportType,
          destinationType: 'YouTube',
          destinationName: status.channel.credsChannelName,
        });
      }
    }

    if (status.phase === 'error') {
      setShowError(true);
    }
  }, [status, teamId, sportType, variant]);

  const handleIntroYes = useCallback(() => {
    analytics.track('Destination Path Choice Selected', {
      pathSelected: 'Yes',
      destinationType: 'YouTube',
      teamId,
    });
    setShowIntro(false);
    signIn();
  }, [teamId, signIn]);

  const handleIntroNo = useCallback(() => {
    analytics.track('Destination Path Choice Selected', {
      pathSelected: 'No',
      destinationType: 'YouTube',
      teamId,
    });
    setShowIntro(false);
    setShowHowTo(true);
  }, [teamId]);

  return {
    signIn,
    cancelSignin,
    status,
    onUnlinkYoutubeClick,
    updateUnlistedSetting,
    isLinked: !!status?.channel,
    ui: {
      showIntro,
      setShowIntro,
      showHowTo,
      setShowHowTo,
      showError,
      setShowError,
      showSuccess,
      setShowSuccess,
      showPrivacyModal,
      setShowPrivacyModal,
      showVideoSettings,
      setShowVideoSettings,
    },
    handlers: {
      handleIntroYes,
      handleIntroNo,
    },
  };
}
